import { setDoc } from "firebase/firestore";

// mobile-v2 PUBLISH side — the cashier mirrors two things into Firestore for the
// Android client:
//
//   customers/{id}/data/mobile          the table layout  { name, type, zones }
//   customers/{id}/data/tables_summary  live occupancy    { "A1": { guests, isLock, items } }
//
// This replaces what the phone used to derive itself: it parsed the bracketed-CSV
// `TableArray` setting into per-zone counts, then read occupancy off the RTDB
// `tables/customercounts` flat int array where 99 meant "locked". Both are gone on
// the Android side; these two docs are the whole contract now.
//
// The phone renders three states from tables_summary:
//   free    — no key for the table, OR the key exists with items === 0
//   active  — items >= 1 (drawn purple)
//   locked  — isLock === true (lock icon; independent of active/free)
// `guests` is display-only and may legitimately be 0 on a busy table (see the
// reconcile note in pos.mobile-v1), so nothing infers free/busy from it.
//
// Cashier-only, like the other cloud sync plugins. Note that the sibling
// cloud.tables-state.plugin.ts has NO such guard; that looks like an oversight, but
// changing it is out of scope here.
export default defineNuxtPlugin(() => {
  const bootstrapStore = useBootstrapStore();
  const shopStore = usePosShopStore();
  const settingsStore = useSettingsStore();
  const jobV1Store = usePosJobsStore();
  const tablesStateStore = usePosTableStatesStore();

  // Last payload written, so an unrelated jobchain recompute doesn't cost a write.
  let lastLayoutJson = "";
  let lastSummaryJson = "";

  let unwatchLayout: (() => void) | undefined;
  let unwatchSummary: (() => void) | undefined;

  const isActive = () => bootstrapStore.loaded && shopStore.runMode === "cashier";

  // ---------------------------------------------------------------- layout

  const buildLayout = () => {
    // settings.computeTable() already turned TableArray into [{name, tableCount}],
    // falling back to a single "T" zone for number-mode shops. Reuse it rather than
    // re-parsing the setting, so the phone's grid and the cashier's always agree.
    const zoneList = (settingsStore.tables || []) as Array<{ name: string; tableCount: number }>;

    const zones: Record<string, number> = {};
    for (const zone of zoneList) {
      if (!zone || !zone.name) continue;
      const count = Number(zone.tableCount) || 0;
      if (count > 0) zones[zone.name] = count;
    }

    // Zone mode is defined by TableArray being present — the same condition
    // computeTable() branches on. In number mode computeTable() emits a single
    // zone named "T", which yields keys T1..Tn and matches tables_summary.
    const tableArray = settingsStore.getSystemEnvByName("TableArray")?.Value || "";

    return {
      name: settingsStore.getStoreName() || "",
      type: tableArray ? "zone" : "number",
      zones,
    };
  };

  const writeLayout = async () => {
    if (!isActive()) return;

    const layout = buildLayout();
    if (Object.keys(layout.zones).length === 0) return; // settings not loaded yet

    const json = JSON.stringify(layout);
    if (json === lastLayoutJson) return;

    try {
      await setDoc(useFirestore().getMobileLayoutRef(), layout);
      lastLayoutJson = json;
      console.log("[cloud.mobile-v2] layout published", layout);
    } catch (e) {
      console.warn("[cloud.mobile-v2] layout publish failed", e);
    }
  };

  // ---------------------------------------------------------------- occupancy

  const buildSummary = () => {
    const summary: Record<string, { guests: number; isLock: boolean; items: number }> = {};

    // Only physical tables (mode 1 / TABLE) belong on the phone's grid — togo, job
    // and delivery cards live in jobV1Store.tables too but have no seat on it.
    //
    // `items` is the line count, and it is what decides "active" on the phone, NOT
    // the presence of the key and NOT guests: a table can sit in jobV1Store.tables
    // with an empty cart, and guests is legitimately 0 on a re-add to an already
    // open table. Line count (rather than summed quantity) matches how the rest of
    // the system asks the same question — see isOpeningTable in pos.mobile-v1 and
    // the totals in jobs.live.plugin — and is immune to the qty-0 line incident.
    const tables = (jobV1Store.tables || {}) as Record<string, any>;
    for (const key of Object.keys(tables)) {
      const table = tables[key];
      if (!table || table.mode !== 1) continue;
      const items = Array.isArray(table.items) ? table.items.length : 0;
      summary[key] = {
        guests: Number(table.guests) || 0,
        isLock: tablesStateStore.isLockedWithTable(key) || false,
        items,
      };
    }

    // A table can be locked while empty, which the pass above would miss. The phone
    // needs the key present so it can draw the lock and refuse the tap — it will
    // still read as free, because items is 0.
    const states = (tablesStateStore.tablesState || {}) as Record<string, any>;
    for (const key of Object.keys(states)) {
      if (!states[key]?.lock) continue;
      if (summary[key]) continue;
      summary[key] = { guests: 0, isLock: true, items: 0 };
    }

    return summary;
  };

  const writeSummary = async () => {
    if (!isActive()) return;

    const summary = buildSummary();
    const json = JSON.stringify(summary);
    if (json === lastSummaryJson) return;

    try {
      // Whole-document set, not merge: a table that frees up has to DISAPPEAR from
      // the doc, and merge would leave the stale key behind forever, pinning the
      // phone's cell to "occupied".
      await setDoc(useFirestore().getTablesSummaryRef(), summary);
      lastSummaryJson = json;
    } catch (e) {
      console.warn("[cloud.mobile-v2] tables_summary publish failed", e);
    }
  };

  // ---------------------------------------------------------------- wiring

  const shouldPublish = computed(() => isActive());

  watch(
    shouldPublish,
    (enabled, previousEnabled) => {
      if (enabled === previousEnabled) return;

      if (enabled) {
        if (!unwatchLayout) {
          unwatchLayout = watch(
            () => settingsStore.tables,
            () => { void writeLayout(); },
            { deep: true, immediate: true }
          );
        }
        if (!unwatchSummary) {
          unwatchSummary = watch(
            [() => jobV1Store.tables, () => tablesStateStore.tablesState],
            () => { void writeSummary(); },
            { deep: true, immediate: true }
          );
        }
        return;
      }

      if (unwatchLayout) { unwatchLayout(); unwatchLayout = undefined; }
      if (unwatchSummary) { unwatchSummary(); unwatchSummary = undefined; }
    },
    { immediate: true }
  );
});
